import { Bell, BellOff, Check, Home, RotateCcw, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface NotificationsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Notification {
  id: string;
  type: 'email' | 'task' | 'deal' | 'contact' | 'system';
  title: string;
  description: string;
  time: string;
  isRead: boolean;
  isDeleted: boolean;
  route?: string;
  sender?: string;
  avatar?: string;
}

const NotificationsSidebar: React.FC<NotificationsSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'unread' | 'all' | 'trash'>('unread');
  const [selectedNotifications, setSelectedNotifications] = useState<string[]>([]);
  const [hoveredNotification, setHoveredNotification] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'email',
      title: 'New unassigned Email conversation from Chris S...',
      description: 'Chris Simon started this conversation: "-------------------------"',
      time: '9h',
      isRead: false,
      isDeleted: false,
      route: '/messages',
      sender: 'Chris Simon'
    },
    {
      id: '2',
      type: 'email',
      title: 'New unassigned Email conversation from Bookf...',
      description: 'Bookface Forum Digest started this conversation: "****"',
      time: '10h',
      isRead: false,
      isDeleted: false,
      route: '/messages',
      sender: 'Bookface Forum'
    },
    {
      id: '3',
      type: 'email',
      title: 'New unassigned Email conversation from Equino...',
      description: 'Equinox Concierge started this conversation: "Hi Tanmay, I ho..."',
      time: '12h',
      isRead: false,
      isDeleted: false,
      route: '/messages',
      sender: 'Equinox Concierge'
    },
    {
      id: '4',
      type: 'email',
      title: 'New unassigned Email conversation from Lizzy ...',
      description: 'Lizzy Riffle started this conversation: "Hi Tanmay, Our monog..."',
      time: '1d',
      isRead: false,
      isDeleted: false,
      route: '/messages',
      sender: 'Lizzy Riffle'
    },
    {
      id: '5',
      type: 'email',
      title: 'New unassigned Email conversation from Alma',
      description: 'Alma started this conversation: "Hi Tanmay, congrats on the s..."',
      time: '1d',
      isRead: false,
      isDeleted: false,
      route: '/messages',
      sender: 'Alma'
    },
    {
      id: '6',
      type: 'email',
      title: 'New unassigned Email conversation from Google...',
      description: 'Google Cloud started this conversation: " Be at the forefront o..."',
      time: '1d',
      isRead: false,
      isDeleted: false,
      route: '/messages',
      sender: 'Google Cloud'
    },
    {
      id: '7',
      type: 'contact',
      title: 'You have been made the Contact owner of the co...',
      description: 'emailmario@hubspot.com',
      time: '1d',
      isRead: false,
      isDeleted: false,
      route: '/contacts',
      sender: 'System'
    },
    {
      id: '8',
      type: 'system',
      title: 'Success!',
      description: 'Your synced contacts are now in HubSpot.',
      time: '2d',
      isRead: false,
      isDeleted: false,
      route: '/contacts',
      sender: 'System'
    }
  ]);

  const filteredNotifications = notifications.filter(notification => {
    switch (activeTab) {
      case 'unread':
        return !notification.isRead && !notification.isDeleted;
      case 'all':
        return !notification.isDeleted;
      case 'trash':
        return notification.isDeleted;
      default:
        return !notification.isDeleted;
    }
  });

  const unreadCount = notifications.filter(n => !n.isRead && !n.isDeleted).length;
  const trashCount = notifications.filter(n => n.isDeleted).length;

  const handleNotificationClick = (notification: Notification) => {
    // Don't navigate if in trash
    if (notification.isDeleted) {
      return;
    }
    
    // Mark as read
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
    );
    
    // Navigate to the relevant page
    if (notification.route) {
      navigate(notification.route);
      onClose();
    }
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.id));
    }
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => 
      prev.map(n => !n.isDeleted ? { ...n, isRead: true } : n)
    );
    setSelectedNotifications([]);
  };

  const handleMarkAsRead = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
  };

  const handleDeleteNotification = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isDeleted: true } : n)
    );
    setSelectedNotifications(prev => prev.filter(id => id !== notificationId));
  };

  const handleMuteNotification = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    // For now, we'll just mark it as read and show a toast
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
    // You could add a toast notification here
    console.log(`🔕 Muted notification: ${notificationId}`);
  };

  const handleRestoreNotification = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isDeleted: false } : n)
    );
  };

  const handlePermanentDelete = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    setSelectedNotifications(prev => prev.filter(id => id !== notificationId));
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'email':
        return '✉️';
      case 'task':
        return '✓';
      case 'deal':
        return '💰';
      case 'contact':
        return '👤';
      case 'system':
        return '🔔';
      default:
        return '📄';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out ${
          isOpen ? 'bg-opacity-50 opacity-100' : 'bg-opacity-0 opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-teal-500 text-white">
          <div className="flex items-center space-x-2">
            <Bell className="h-5 w-5" />
            <h2 className="text-lg font-semibold">Notifications</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-teal-600 rounded-md transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('unread')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'unread'
                ? 'border-teal-500 text-teal-600 bg-teal-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Unread ({unreadCount})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-teal-500 text-teal-600 bg-teal-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All
          </button>
            <button
              onClick={() => setActiveTab('trash')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'trash'
                  ? 'border-teal-500 text-teal-600 bg-teal-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Trash {trashCount > 0 && `(${trashCount})`}
            </button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={selectedNotifications.length === filteredNotifications.length && filteredNotifications.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-600">
              Select all {filteredNotifications.length > 0 && (
                <span className="inline-flex items-center space-x-1">
                  <span className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs text-blue-600">ℹ</span>
                  </span>
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <button 
              onClick={handleMarkAllAsRead}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Mark all as read"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Bell className="h-12 w-12 mb-4 text-gray-300" />
              <p className="text-lg font-medium">No notifications</p>
              <p className="text-sm">You're all caught up!</p>
            </div>
          ) : (
            <div className="space-y-0">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`relative flex items-start space-x-3 p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.isRead ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                  onMouseEnter={() => setHoveredNotification(notification.id)}
                  onMouseLeave={() => setHoveredNotification(null)}
                >
                  <input
                    type="checkbox"
                    checked={selectedNotifications.includes(notification.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (e.target.checked) {
                        setSelectedNotifications(prev => [...prev, notification.id]);
                      } else {
                        setSelectedNotifications(prev => prev.filter(id => id !== notification.id));
                      }
                    }}
                    className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  
                  <div className="flex-shrink-0 mt-1">
                    <span className="text-lg">{getNotificationIcon(notification.type)}</span>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <p className={`text-sm ${!notification.isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                        {notification.title}
                      </p>
                      <div className="flex items-center space-x-1">
                        <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                          {notification.time}
                        </span>
                        
                        {/* Hover Actions */}
                        {hoveredNotification === notification.id && (
                          <div className="flex items-center space-x-1 ml-2">
                            {activeTab === 'trash' ? (
                              // Trash tab actions
                              <>
                                <button
                                  onClick={(e) => handleRestoreNotification(notification.id, e)}
                                  className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                                  title="Restore notification"
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={(e) => handlePermanentDelete(notification.id, e)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete permanently"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </>
                            ) : (
                              // Regular tab actions
                              <>
                                {!notification.isRead && (
                                  <button
                                    onClick={(e) => handleMarkAsRead(notification.id, e)}
                                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Mark as read"
                                  >
                                    <Home className="h-3 w-3" />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => handleDeleteNotification(notification.id, e)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Move to trash"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={(e) => handleMuteNotification(notification.id, e)}
                                  className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                                  title="Turn off notifications"
                                >
                                  <BellOff className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {notification.description}
                    </p>
                    {notification.sender && (
                      <p className="text-xs text-gray-400 mt-1">
                        {notification.sender}
                      </p>
                    )}
                    {!notification.isRead && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {/* <div className="p-3 border-t border-gray-200 bg-gray-50">
          <button className="w-full text-center text-sm text-teal-600 hover:text-teal-800 font-medium">
            Not showing here?
          </button>
        </div> */}
      </div>
    </>
  );
};

export default NotificationsSidebar;
